import { LitElement, html, css } from 'lit';

// Conversion Helper Functions
function hsvToRgb(h, s, v) {
  s /= 100;
  v /= 100;
  const k = (n) => (n + h / 60) % 6;
  const f = (n) => v * (1 - s * Math.max(0, Math.min(k(n), 4 - k(n), 1)));
  return {
    r: Math.round(255 * f(5)),
    g: Math.round(255 * f(3)),
    b: Math.round(255 * f(1))
  };
}

function rgbToHsv(r, g, b) {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else if (max === b) h = (r - g) / d + 4;
    h = Math.round(h * 60);
  }
  const s = max === 0 ? 0 : (d / max) * 100;
  const v = max * 100;
  return { h, s: Math.round(s), v: Math.round(v) };
}

function rgbToHex(r, g, b, a = 1) {
  const toHex = (x) => {
    const hex = Math.round(x).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  const alphaHex = a < 1 ? toHex(a * 255) : '';
  return `#${toHex(r)}${toHex(g)}${toHex(b)}${alphaHex}`;
}

function parseHex(hex) {
  hex = hex.trim().replace(/^#/, '');
  if (hex.length === 3 || hex.length === 4) {
    hex = hex.split('').map(char => char + char).join('');
  }
  if (hex.length === 6) {
    return {
      r: parseInt(hex.substring(0, 2), 16),
      g: parseInt(hex.substring(2, 4), 16),
      b: parseInt(hex.substring(4, 6), 16),
      a: 1
    };
  } else if (hex.length === 8) {
    return {
      r: parseInt(hex.substring(0, 2), 16),
      g: parseInt(hex.substring(2, 4), 16),
      b: parseInt(hex.substring(4, 6), 16),
      a: Math.round((parseInt(hex.substring(6, 8), 16) / 255) * 100) / 100
    };
  }
  return null;
}

function hslToRgb(h, s, l) {
  s /= 100;
  l /= 100;
  const k = n => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = n => l - a * Math.max(-1, Math.min(k(n) - 3, 9 - k(n), 1));
  return {
    r: Math.round(255 * f(0)),
    g: Math.round(255 * f(8)),
    b: Math.round(255 * f(4))
  };
}

function rgbToHsl(r, g, b) {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100)
  };
}

function parseColor(str) {
  if (!str) return null;
  str = str.trim().toLowerCase();
  
  if (str.startsWith('#') || /^[0-9a-f]{3,8}$/.test(str)) {
    return parseHex(str);
  }
  
  const rgbMatch = str.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+)\s*)?\)$/);
  if (rgbMatch) {
    return {
      r: parseInt(rgbMatch[1], 10),
      g: parseInt(rgbMatch[2], 10),
      b: parseInt(rgbMatch[3], 10),
      a: rgbMatch[4] !== undefined ? parseFloat(rgbMatch[4]) : 1
    };
  }
  
  const hslMatch = str.match(/^hsla?\(\s*([\d.]+)\s*,\s*([\d.]+)%\s*,\s*([\d.]+)%\s*(?:,\s*([\d.]+)\s*)?\)$/);
  if (hslMatch) {
    const h = parseFloat(hslMatch[1]);
    const s = parseFloat(hslMatch[2]);
    const l = parseFloat(hslMatch[3]);
    const a = hslMatch[4] !== undefined ? parseFloat(hslMatch[4]) : 1;
    const rgb = hslToRgb(h, s, l);
    return { ...rgb, a };
  }
  
  return null;
}

export class UIColorPicker extends LitElement {
  static formAssociated = true;

  static properties = {
    name: { type: String },
    value: { type: String, reflect: true },
    placeholder: { type: String },
    disabled: { type: Boolean, reflect: true },
    required: { type: Boolean, reflect: true },
    format: { type: String }, // 'hex', 'rgb', 'hsl'
    presets: { type: Array },
    label: { type: String, reflect: true },
    
    // Internal States
    isOpen: { type: Boolean, state: true },
    inputValue: { type: String, state: true }
  };

  static styles = css`
    :host {
      display: inline-block;
      width: 100%;
      position: relative;
      font-family: var(--ui-font-family);
      font-size: var(--ui-font-size-base);
      color: var(--ui-text);
    }

    .ui-label {
      display: block;
      margin-bottom: 6px;
      font-size: var(--ui-font-size-sm);
      font-weight: 500;
      color: var(--ui-text-muted);
      text-align: left;
    }

    .colorpicker-container {
      position: relative;
      width: 100%;
    }

    /* main input trigger */
    .colorpicker-header {
      position: relative;
      display: flex;
      align-items: center;
      width: 100%;
    }

    .colorpicker-input {
      width: 100%;
      height: var(--ui-input-height);
      padding: 0 var(--ui-padding-x);
      padding-left: 48px;
      background-color: var(--ui-surface);
      border: 1px solid var(--ui-border);
      border-radius: var(--ui-radius-md);
      font-family: inherit;
      font-size: inherit;
      color: inherit;
      box-shadow: var(--ui-shadow-sm);
      transition: var(--ui-transition-fast);
      outline: none;
      box-sizing: border-box;
    }

    .colorpicker-input:hover:not(:disabled) {
      border-color: var(--ui-border-hover);
      background-color: var(--ui-surface-hover);
    }

    .colorpicker-input:focus:not(:disabled) {
      border-color: var(--ui-border-focus);
      background-color: var(--ui-surface);
      box-shadow: 0 0 0 4px var(--ui-focus-ring);
    }

    .colorpicker-input:disabled {
      cursor: not-allowed;
      opacity: 0.6;
      background-color: var(--ui-surface-active);
    }

    /* Color Swatch Trigger */
    .color-swatch-trigger {
      position: absolute;
      left: 12px;
      top: 50%;
      transform: translateY(-50%);
      width: 24px;
      height: 24px;
      border-radius: var(--ui-radius-sm);
      border: 1px solid var(--ui-border);
      cursor: pointer;
      box-shadow: var(--ui-shadow-sm);
      background-image: linear-gradient(45deg, #ccc 25%, transparent 25%),
                        linear-gradient(-45deg, #ccc 25%, transparent 25%),
                        linear-gradient(45deg, transparent 75%, #ccc 75%),
                        linear-gradient(-45deg, transparent 75%, #ccc 75%);
      background-size: 8px 8px;
      background-position: 0 0, 0 4px, 4px -4px, -4px 0;
      overflow: hidden;
    }

    .color-swatch-trigger::after {
      content: '';
      display: block;
      width: 100%;
      height: 100%;
      background-color: var(--swatch-color, transparent);
      box-sizing: border-box;
    }

    /* Popover panel */
    .colorpicker-panel {
      position: absolute;
      top: calc(100% + 6px);
      left: 0;
      width: 280px;
      padding: 16px;
      background-color: color-mix(in srgb, var(--ui-surface) 90%, transparent);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      border: 1px solid var(--ui-border);
      border-radius: var(--ui-radius-lg);
      box-shadow: var(--ui-shadow-popover);
      z-index: var(--ui-z-popover);
      transform-origin: top;
      opacity: 0;
      transform: scale(0.95);
      pointer-events: none;
      transition: opacity var(--ui-transition-fast), transform 300ms cubic-bezier(0.34, 1.56, 0.64, 1);
      box-sizing: border-box;
    }

    .colorpicker-panel.open {
      opacity: 1;
      transform: scale(1);
      pointer-events: auto;
    }

    /* Saturation & Value 2D Box */
    .color-grid {
      position: relative;
      width: 100%;
      height: 140px;
      border-radius: var(--ui-radius-md);
      cursor: crosshair;
      overflow: hidden;
      margin-bottom: 12px;
    }

    .color-grid-hue-layer {
      width: 100%;
      height: 100%;
      background-color: hsl(var(--current-hue, 0), 100%, 50%);
    }

    .color-grid-saturation-layer {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-image: linear-gradient(to right, #fff, transparent);
    }

    .color-grid-value-layer {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-image: linear-gradient(to top, #000, transparent);
    }

    .color-grid-handle {
      position: absolute;
      width: 12px;
      height: 12px;
      border: 2px solid #fff;
      border-radius: var(--ui-radius-full);
      box-shadow: 0 0 0 1px rgba(0,0,0,0.5), var(--ui-shadow-sm);
      transform: translate(-50%, -50%);
      pointer-events: none;
    }

    /* Sliders styling */
    .sliders-container {
      display: flex;
      flex-direction: column;
      gap: 10px;
      margin-bottom: 14px;
    }

    .slider-row {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .slider-track {
      position: relative;
      flex: 1;
      height: 10px;
      border-radius: var(--ui-radius-full);
      cursor: pointer;
      border: 1px solid var(--ui-border);
    }

    .slider-handle {
      position: absolute;
      top: 50%;
      width: 14px;
      height: 14px;
      background-color: #fff;
      border: 2px solid var(--ui-primary);
      border-radius: var(--ui-radius-full);
      box-shadow: var(--ui-shadow-sm);
      transform: translate(-50%, -50%);
      pointer-events: none;
    }

    .hue-slider-track {
      background-image: linear-gradient(to right, 
        #ff0000 0%, #ffff00 17%, #00ff00 33%, 
        #00ffff 50%, #0000ff 67%, #ff00ff 83%, #ff0000 100%
      );
    }

    .alpha-slider-track-wrap {
      position: relative;
      flex: 1;
      height: 10px;
      border-radius: var(--ui-radius-full);
      background-image: linear-gradient(45deg, #ccc 25%, transparent 25%),
                        linear-gradient(-45deg, #ccc 25%, transparent 25%),
                        linear-gradient(45deg, transparent 75%, #ccc 75%),
                        linear-gradient(-45deg, transparent 75%, #ccc 75%);
      background-size: 6px 6px;
      background-position: 0 0, 0 3px, 3px -3px, -3px 0;
      border: 1px solid var(--ui-border);
      overflow: hidden;
    }

    .alpha-slider-track {
      width: 100%;
      height: 100%;
      cursor: pointer;
      background-image: linear-gradient(to right, transparent, var(--alpha-color-solid, #6366f1));
    }

    /* Small Swatch inside panel */
    .panel-color-preview {
      width: 24px;
      height: 24px;
      border-radius: var(--ui-radius-sm);
      border: 1px solid var(--ui-border);
      background-image: linear-gradient(45deg, #ccc 25%, transparent 25%),
                        linear-gradient(-45deg, #ccc 25%, transparent 25%),
                        linear-gradient(45deg, transparent 75%, #ccc 75%),
                        linear-gradient(-45deg, transparent 75%, #ccc 75%);
      background-size: 6px 6px;
      background-position: 0 0, 0 3px, 3px -3px, -3px 0;
      position: relative;
      overflow: hidden;
    }

    .panel-color-preview-inner {
      width: 100%;
      height: 100%;
      background-color: var(--swatch-color, transparent);
    }

    /* Direct text input row */
    .color-inputs-row {
      display: grid;
      grid-template-columns: 2fr 1fr 1fr 1fr 1.2fr;
      gap: 4px;
      margin-bottom: 14px;
    }

    .color-inputs-row input {
      width: 100%;
      height: 28px;
      background-color: var(--ui-surface);
      border: 1px solid var(--ui-border);
      border-radius: var(--ui-radius-sm);
      font-size: 11px;
      text-align: center;
      color: var(--ui-text);
      outline: none;
      padding: 0;
    }

    .color-inputs-row input:focus {
      border-color: var(--ui-primary);
    }

    .color-inputs-row span {
      display: block;
      font-size: 9px;
      color: var(--ui-text-muted);
      text-align: center;
      margin-top: 2px;
    }

    /* Presets */
    .presets-title {
      font-size: 11px;
      font-weight: 600;
      color: var(--ui-text-muted);
      margin-bottom: 6px;
    }

    .presets-grid {
      display: grid;
      grid-template-columns: repeat(6, 1fr);
      gap: 8px;
    }

    .preset-chip {
      aspect-ratio: 1;
      border-radius: var(--ui-radius-sm);
      border: 1px solid var(--ui-border);
      cursor: pointer;
      transition: transform var(--ui-transition-fast), border-color var(--ui-transition-fast);
      background-image: linear-gradient(45deg, #ccc 25%, transparent 25%),
                        linear-gradient(-45deg, #ccc 25%, transparent 25%),
                        linear-gradient(45deg, transparent 75%, #ccc 75%),
                        linear-gradient(-45deg, transparent 75%, #ccc 75%);
      background-size: 6px 6px;
      background-position: 0 0, 0 3px, 3px -3px, -3px 0;
      overflow: hidden;
    }

    .preset-chip-inner {
      width: 100%;
      height: 100%;
    }

    .preset-chip:hover {
      transform: scale(1.1);
      border-color: var(--ui-primary);
    }

    /* Mobile bottom sheet */
    .backdrop {
      display: none;
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background-color: rgba(0, 0, 0, 0.4);
      backdrop-filter: blur(4px);
      z-index: calc(var(--ui-z-popover) - 1);
    }

    @media (max-width: 576px) {
      .colorpicker-container.open .backdrop {
        display: block;
      }

      .colorpicker-panel {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        top: auto;
        width: 100%;
        max-width: 100%;
        border-radius: var(--ui-radius-lg) var(--ui-radius-lg) 0 0;
        box-shadow: 0 -10px 25px rgba(0,0,0,0.15);
        transform: translateY(100%);
        transform-origin: bottom;
        transition: transform var(--ui-transition-normal) cubic-bezier(0.25, 0.8, 0.25, 1), opacity var(--ui-transition-normal);
        z-index: var(--ui-z-popover);
      }

      .colorpicker-container.open .colorpicker-panel {
        transform: translateY(0);
        opacity: 1;
      }

      .colorpicker-panel::before {
        content: '';
        display: block;
        width: 40px;
        height: 4px;
        background-color: var(--ui-border);
        border-radius: var(--ui-radius-full);
        margin: -8px auto 16px auto;
      }
    }
  `;

  constructor() {
    super();
    this.internals = this.attachInternals();
    this.name = '';
    this.value = '';
    this.placeholder = 'Sélectionner une couleur...';
    this.disabled = false;
    this.required = false;
    this.format = 'hex'; // 'hex', 'rgb', 'hsl'
    this.label = '';
    
    // Default 12 high-end presets
    this.presets = [
      '#6366f1', // Indigo
      '#8b5cf6', // Violet
      '#ec4899', // Pink
      '#ef4444', // Red
      '#f97316', // Orange
      '#f59e0b', // Amber
      '#10b981', // Green
      '#14b8a6', // Teal
      '#06b6d4', // Cyan
      '#0ea5e9', // Sky
      '#64748b', // Slate
      '#ffffff'  // White
    ];

    this.isOpen = false;
    this.inputValue = '';

    // Internals color state
    this._h = 239; // Hue (0-360)
    this._s = 59;  // Saturation (0-100)
    this._v = 95;  // Brightness/Value (0-100)
    this._a = 1.0; // Alpha (0-1)
    
    this._r = 99;
    this._g = 102;
    this._b = 241;

    this._handleOutsideClick = this._handleOutsideClick.bind(this);
    this._onGridMouseMove = this._onGridMouseMove.bind(this);
    this._onGridMouseUp = this._onGridMouseUp.bind(this);
    this._onHueMouseMove = this._onHueMouseMove.bind(this);
    this._onHueMouseUp = this._onHueMouseUp.bind(this);
    this._onAlphaMouseMove = this._onAlphaMouseMove.bind(this);
    this._onAlphaMouseUp = this._onAlphaMouseUp.bind(this);
  }

  willUpdate(changedProperties) {
    if (typeof this.presets === 'string') {
      try {
        this.presets = JSON.parse(this.presets);
      } catch (e) {
        this.presets = [];
      }
    }

    if (changedProperties.has('value')) {
      if (this.value) {
        this._parseAndSyncValue(this.value);
      } else {
        this.inputValue = '';
      }
    }
  }

  get validity() { return this.internals.validity; }
  get validationMessage() { return this.internals.validationMessage; }
  get willValidate() { return this.internals.willValidate; }
  checkValidity() { return this.internals.checkValidity(); }
  reportValidity() { return this.internals.reportValidity(); }

  updated(changedProperties) {
    if (changedProperties.has('value') || changedProperties.has('required')) {
      this.internals.setFormValue(this.value);
      this._updateValidity();
    }
  }

  _updateValidity() {
    const hasValue = this.value && this.value.trim() !== '';
    if (this.required && !hasValue) {
      this.internals.setValidity({ valueMissing: true }, 'Veuillez choisir une couleur.', this.shadowRoot.querySelector('.colorpicker-input'));
    } else {
      this.internals.setValidity({});
    }
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('mousedown', this._handleOutsideClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('mousedown', this._handleOutsideClick);
  }

  _parseAndSyncValue(val) {
    const parsed = parseColor(val);
    if (parsed) {
      this._r = parsed.r;
      this._g = parsed.g;
      this._b = parsed.b;
      this._a = parsed.a;
      
      const hsv = rgbToHsv(parsed.r, parsed.g, parsed.b);
      this._h = hsv.h;
      this._s = hsv.s;
      this._v = hsv.v;
      
      this.inputValue = val;
    } else {
      this.inputValue = '';
    }
  }

  _handleOutsideClick(e) {
    if (this.isOpen && !this.contains(e.target) && !e.composedPath().includes(this)) {
      this._closePanel();
    }
  }

  _togglePanel() {
    if (this.disabled) return;
    if (this.isOpen) {
      this._closePanel();
    } else {
      this._openPanel();
    }
  }

  _openPanel() {
    this.isOpen = true;
    if (this.value) {
      this._parseAndSyncValue(this.value);
    } else {
      // Initialize with Indigo default if empty
      this._updateHSV({ h: 239, s: 59, v: 95, a: 1.0 });
    }
  }

  _closePanel() {
    this.isOpen = false;
  }

  _updateHSV(newHSV) {
    if (newHSV.h !== undefined) this._h = newHSV.h;
    if (newHSV.s !== undefined) this._s = newHSV.s;
    if (newHSV.v !== undefined) this._v = newHSV.v;
    if (newHSV.a !== undefined) this._a = newHSV.a;
    
    const rgb = hsvToRgb(this._h, this._s, this._v);
    this._r = rgb.r;
    this._g = rgb.g;
    this._b = rgb.b;
    
    this._formatAndSetValue();
    this.requestUpdate();
  }

  _formatAndSetValue() {
    let newVal;
    if (this.format === 'rgb') {
      newVal = this._a === 1 ? `rgb(${this._r}, ${this._g}, ${this._b})` : `rgba(${this._r}, ${this._g}, ${this._b}, ${this._a})`;
    } else if (this.format === 'hsl') {
      const hsl = rgbToHsl(this._r, this._g, this._b);
      newVal = this._a === 1 ? `hsl(${hsl.h}, ${hsl.s}%, ${hsl.l}%)` : `hsla(${hsl.h}, ${hsl.s}%, ${hsl.l}%, ${this._a})`;
    } else {
      newVal = rgbToHex(this._r, this._g, this._b, this._a);
    }
    
    if (newVal !== this.value) {
      this.value = newVal;
      this.inputValue = newVal;
      this._dispatchChangeEvent(newVal);
    }
  }

  _dispatchChangeEvent(val) {
    this.dispatchEvent(new CustomEvent('change', {
      detail: { value: val },
      bubbles: true,
      composed: true
    }));
  }

  _handleInput(e) {
    const val = e.target.value;
    this.inputValue = val;
    const parsed = parseColor(val);
    if (parsed) {
      this._r = parsed.r;
      this._g = parsed.g;
      this._b = parsed.b;
      this._a = parsed.a;
      
      const hsv = rgbToHsv(parsed.r, parsed.g, parsed.b);
      this._h = hsv.h;
      this._s = hsv.s;
      this._v = hsv.v;
      
      this.value = val;
      this.internals.setFormValue(this.value);
      this._updateValidity();
      this.requestUpdate();
    }
  }

  _handleBlur() {
    if (this.value) {
      this._parseAndSyncValue(this.value);
    } else {
      this.inputValue = '';
    }
  }

  // 2D Grid Drag Logic
  _onGridMouseDown(e) {
    e.preventDefault();
    this._isDraggingGrid = true;
    this._updateGridColorFromCoords(e);
    document.addEventListener('mousemove', this._onGridMouseMove);
    document.addEventListener('mouseup', this._onGridMouseUp);
  }

  _onGridMouseMove(e) {
    if (!this._isDraggingGrid) return;
    this._updateGridColorFromCoords(e);
  }

  _onGridMouseUp() {
    this._isDraggingGrid = false;
    document.removeEventListener('mousemove', this._onGridMouseMove);
    document.removeEventListener('mouseup', this._onGridMouseUp);
  }

  _onGridTouchStart(e) {
    this._isDraggingGrid = true;
    this._updateGridColorFromCoords(e.touches[0]);
    
    const onTouchMove = (moveEvent) => {
      if (!this._isDraggingGrid) return;
      this._updateGridColorFromCoords(moveEvent.touches[0]);
    };
    
    const onTouchEnd = () => {
      this._isDraggingGrid = false;
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
    };
    
    document.addEventListener('touchmove', onTouchMove);
    document.addEventListener('touchend', onTouchEnd);
  }

  _updateGridColorFromCoords(e) {
    const rect = this.shadowRoot.querySelector('.color-grid').getBoundingClientRect();
    let x = e.clientX - rect.left;
    let y = e.clientY - rect.top;
    
    x = Math.max(0, Math.min(x, rect.width));
    y = Math.max(0, Math.min(y, rect.height));
    
    const s = Math.round((x / rect.width) * 100);
    const v = Math.round((1 - y / rect.height) * 100);
    
    this._updateHSV({ s, v });
  }

  // Hue Slider Drag Logic
  _onHueMouseDown(e) {
    e.preventDefault();
    this._isDraggingHue = true;
    this._updateHueFromCoords(e);
    document.addEventListener('mousemove', this._onHueMouseMove);
    document.addEventListener('mouseup', this._onHueMouseUp);
  }

  _onHueMouseMove(e) {
    if (!this._isDraggingHue) return;
    this._updateHueFromCoords(e);
  }

  _onHueMouseUp() {
    this._isDraggingHue = false;
    document.removeEventListener('mousemove', this._onHueMouseMove);
    document.removeEventListener('mouseup', this._onHueMouseUp);
  }

  _onHueTouchStart(e) {
    this._isDraggingHue = true;
    this._updateHueFromCoords(e.touches[0]);
    
    const onTouchMove = (moveEvent) => {
      if (!this._isDraggingHue) return;
      this._updateHueFromCoords(moveEvent.touches[0]);
    };
    
    const onTouchEnd = () => {
      this._isDraggingHue = false;
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
    };
    
    document.addEventListener('touchmove', onTouchMove);
    document.addEventListener('touchend', onTouchEnd);
  }

  _updateHueFromCoords(e) {
    const rect = this.shadowRoot.querySelector('.hue-slider-track').getBoundingClientRect();
    let x = e.clientX - rect.left;
    x = Math.max(0, Math.min(x, rect.width));
    const h = Math.round((x / rect.width) * 360);
    this._updateHSV({ h });
  }

  // Alpha Slider Drag Logic
  _onAlphaMouseDown(e) {
    e.preventDefault();
    this._isDraggingAlpha = true;
    this._updateAlphaFromCoords(e);
    document.addEventListener('mousemove', this._onAlphaMouseMove);
    document.addEventListener('mouseup', this._onAlphaMouseUp);
  }

  _onAlphaMouseMove(e) {
    if (!this._isDraggingAlpha) return;
    this._updateAlphaFromCoords(e);
  }

  _onAlphaMouseUp() {
    this._isDraggingAlpha = false;
    document.removeEventListener('mousemove', this._onAlphaMouseMove);
    document.removeEventListener('mouseup', this._onAlphaMouseUp);
  }

  _onAlphaTouchStart(e) {
    this._isDraggingAlpha = true;
    this._updateAlphaFromCoords(e.touches[0]);
    
    const onTouchMove = (moveEvent) => {
      if (!this._isDraggingAlpha) return;
      this._updateAlphaFromCoords(moveEvent.touches[0]);
    };
    
    const onTouchEnd = () => {
      this._isDraggingAlpha = false;
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
    };
    
    document.addEventListener('touchmove', onTouchMove);
    document.addEventListener('touchend', onTouchEnd);
  }

  _updateAlphaFromCoords(e) {
    const rect = this.shadowRoot.querySelector('.alpha-slider-track').getBoundingClientRect();
    let x = e.clientX - rect.left;
    x = Math.max(0, Math.min(x, rect.width));
    const a = Math.round((x / rect.width) * 100) / 100;
    this._updateHSV({ a });
  }

  // Manual Inputs handler
  _onTextChannelChange(channel, e) {
    let val = parseFloat(e.target.value);
    if (isNaN(val)) return;

    if (channel === 'r' || channel === 'g' || channel === 'b') {
      val = Math.max(0, Math.min(255, Math.round(val)));
      this[`_${channel}`] = val;
      const hsv = rgbToHsv(this._r, this._g, this._b);
      this._h = hsv.h;
      this._s = hsv.s;
      this._v = hsv.v;
    } else if (channel === 'a') {
      val = Math.max(0, Math.min(1, val));
      this._a = val;
    } else if (channel === 'h') {
      val = Math.max(0, Math.min(360, Math.round(val)));
      this._h = val;
      const rgb = hsvToRgb(this._h, this._s, this._v);
      this._r = rgb.r;
      this._g = rgb.g;
      this._b = rgb.b;
    } else if (channel === 's' || channel === 'v') {
      val = Math.max(0, Math.min(100, Math.round(val)));
      this[`_${channel}`] = val;
      const rgb = hsvToRgb(this._h, this._s, this._v);
      this._r = rgb.r;
      this._g = rgb.g;
      this._b = rgb.b;
    }

    this._formatAndSetValue();
    this.requestUpdate();
  }

  _onHexInputChange(e) {
    const parsed = parseHex(e.target.value);
    if (parsed) {
      this._r = parsed.r;
      this._g = parsed.g;
      this._b = parsed.b;
      this._a = parsed.a;
      
      const hsv = rgbToHsv(parsed.r, parsed.g, parsed.b);
      this._h = hsv.h;
      this._s = hsv.s;
      this._v = hsv.v;
      
      this._formatAndSetValue();
      this.requestUpdate();
    }
  }

  _selectPreset(presetColor) {
    this._parseAndSyncValue(presetColor);
    this._formatAndSetValue();
  }

  // Keyboard navigation on component
  _handleKeyDown(e) {
    if (e.key === 'Escape') {
      this._closePanel();
    } else if (e.key === 'Enter') {
      if (this.isOpen) {
        this._closePanel();
      } else {
        this._openPanel();
      }
      e.preventDefault();
    }
  }

  render() {
    const solidRGBColor = `rgb(${this._r}, ${this._g}, ${this._b})`;
    const currentHex = rgbToHex(this._r, this._g, this._b);
    
    // Percentages for handles
    const handleLeft = `${this._s}%`;
    const handleTop = `${100 - this._v}%`;
    const hueHandleLeft = `${(this._h / 360) * 100}%`;
    const alphaHandleLeft = `${this._a * 100}%`;

    return html`
      ${this.label ? html`<label class="ui-label" part="label">${this.label}</label>` : ''}
      <div 
        class="colorpicker-container ${this.isOpen ? 'open' : ''}" 
        style="
          --current-hue: ${this._h};
          --swatch-color: ${this.value || 'transparent'};
          --alpha-color-solid: ${solidRGBColor};
        "
      >
        <div class="colorpicker-header">
          <div 
            class="color-swatch-trigger" 
            @click="${this._togglePanel}"
            title="Choisir une couleur"
          ></div>
          <input
            type="text"
            inputmode="none"
            class="colorpicker-input" part="input"
            .value="${this.inputValue}"
            placeholder="${this.placeholder}"
            ?disabled="${this.disabled}"
            @input="${this._handleInput}"
            @blur="${this._handleBlur}"
            @keydown="${this._handleKeyDown}"
          />
        </div>

        <div class="backdrop" @click="${this._closePanel}"></div>

        <div class="colorpicker-panel ${this.isOpen ? 'open' : ''}">
          <!-- 2D saturation/brightness selector -->
          <div 
            class="color-grid" 
            @mousedown="${this._onGridMouseDown}"
            @touchstart="${this._onGridTouchStart}"
          >
            <div class="color-grid-hue-layer"></div>
            <div class="color-grid-saturation-layer"></div>
            <div class="color-grid-value-layer"></div>
            <div class="color-grid-handle" style="left: ${handleLeft}; top: ${handleTop};"></div>
          </div>

          <!-- Sliders and Preview Swatch -->
          <div class="sliders-container">
            <div class="slider-row">
              <div class="panel-color-preview">
                <div class="panel-color-preview-inner"></div>
              </div>
              
              <!-- Hue Slider -->
              <div 
                class="slider-track hue-slider-track" 
                @mousedown="${this._onHueMouseDown}"
                @touchstart="${this._onHueTouchStart}"
              >
                <div class="slider-handle" style="left: ${hueHandleLeft};"></div>
              </div>
            </div>

            <!-- Alpha Slider -->
            <div class="slider-row">
              <div style="width: 24px;"></div> <!-- align with top row preview -->
              <div class="alpha-slider-track-wrap">
                <div 
                  class="alpha-slider-track"
                  @mousedown="${this._onAlphaMouseDown}"
                  @touchstart="${this._onAlphaTouchStart}"
                >
                  <div class="slider-handle" style="left: ${alphaHandleLeft};"></div>
                </div>
              </div>
            </div>
          </div>

          <!-- Color Inputs (HEX, R, G, B, A) -->
          <div class="color-inputs-row">
            <div>
              <input type="text" .value="${currentHex}" @change="${this._onHexInputChange}" />
              <span>HEX</span>
            </div>
            <div>
              <input type="number" .value="${this._r}" @input="${(e) => this._onTextChannelChange('r', e)}" min="0" max="255" />
              <span>R</span>
            </div>
            <div>
              <input type="number" .value="${this._g}" @input="${(e) => this._onTextChannelChange('g', e)}" min="0" max="255" />
              <span>G</span>
            </div>
            <div>
              <input type="number" .value="${this._b}" @input="${(e) => this._onTextChannelChange('b', e)}" min="0" max="255" />
              <span>B</span>
            </div>
            <div>
              <input type="number" .value="${this._a}" step="0.1" @input="${(e) => this._onTextChannelChange('a', e)}" min="0" max="1" />
              <span>A</span>
            </div>
          </div>

          <!-- Presets -->
          ${Array.isArray(this.presets) && this.presets.length > 0 ? html`
            <div class="presets-title">Couleurs recommandées</div>
            <div class="presets-grid">
              ${this.presets.slice(0, 12).map(c => html`
                <div 
                  class="preset-chip" 
                  @click="${() => this._selectPreset(c)}"
                  title="${c}"
                >
                  <div class="preset-chip-inner" style="background-color: ${c};"></div>
                </div>
              `)}
            </div>
          ` : ''}
        </div>
      </div>
    `;
  }
}

if (!customElements.get('ui-colorpicker')) {
  customElements.define('ui-colorpicker', UIColorPicker);
}
